import {
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  UserIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { Spinner } from "@via/components/loading/loading.tsx";
import { useCreateAccount } from "@via/pages/authentication/self-service-signup/authentication.api.tsx";
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_RULES,
  maxLengthValidation,
  requiredValidation,
} from "@via/utils/form-validators.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { CreateAccountRequestBody } from "common";
import React, { useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import LoginLink from "@via/components/login-link.tsx";

const passwordCriteriaList = [
  {
    id: "length",
    text: `${PASSWORD_MIN_LENGTH} characters minimum`,
    test: (v: string) => v.length >= PASSWORD_MIN_LENGTH,
  },
  ...PASSWORD_RULES.map(rule => ({
    id: rule.key,
    text: rule.message,
    test: (v: string) => rule.pattern.test(v),
  })),
];

interface StepProps {
  onNextStep: () => void;
  email: string;
}

const AccountInfoForm: React.FC<StepProps> = ({ onNextStep, email }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const {
    mutate: createAccount,
    isPending: isCreateAccountLoading,
    isSuccess: isCreateAccountSuccess,
  } = useCreateAccount();

  useEffect(() => {
    if (isCreateAccountSuccess) {
      onNextStep();
    }
  }, [isCreateAccountSuccess]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<CreateAccountRequestBody>({
    mode: "onChange",
  });

  const passwordValue = watch("password", "");

  const passwordCriteriaMet = useMemo(() => {
    const criteria: { [key: string]: boolean } = {};
    passwordCriteriaList.forEach(c => {
      criteria[c.id] = c.test(passwordValue);
    });
    return criteria;
  }, [passwordValue]);

  const isPasswordStrong = Object.values(passwordCriteriaMet).every(Boolean);

  const onSubmit: SubmitHandler<CreateAccountRequestBody> = data => {
    createAccount({
      requestQueryParams: {
        token: token ?? undefined,
      },
      requestBody: {
        ...data,
        email,
      },
    });
  };

  return (
    <div className="w-full max-w-md p-8 pt-1 space-y-8">
      <h2 className="text-3xl font-bold text-center text-gray-900">
        Create your account
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label
            htmlFor="username"
            className="text-sm font-medium text-gray-700">
            Username
          </label>
          <div className="relative mt-1">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
              <UserIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
            </span>
            <input
              id="username"
              type="text"
              placeholder="Username"
              {...register("username", {
                ...requiredValidation("Username"),
                ...maxLengthValidation(
                  40,
                  "Username must be less than 40 characters"
                ),
              })}
              className={`block w-full py-3 pl-10 pr-3 text-gray-900 placeholder-gray-400 border rounded-md shadow-sm appearance-none focus:outline-none focus:ring-2 sm:text-sm 
                ${
                  errors.username
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                }`}
            />
          </div>
          {errors.username && (
            <p className="mt-2 text-sm text-red-600">
              {errors.username.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="firstName"
            className="text-sm font-medium text-gray-700">
            First Name
          </label>
          <div className="relative mt-1">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
              <UserIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
            </span>
            <input
              id="firstName"
              type="text"
              placeholder="First Name"
              {...register("firstName", {
                ...requiredValidation("First Name"),
                ...maxLengthValidation(
                  40,
                  "First name must be less than 40 characters"
                ),
              })}
              className={`block w-full py-3 pl-10 pr-3 text-gray-900 placeholder-gray-400 border rounded-md shadow-sm appearance-none focus:outline-none focus:ring-2 sm:text-sm 
                ${
                  errors.firstName
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                }`}
            />
          </div>
          {errors.firstName && (
            <p className="mt-2 text-sm text-red-600">
              {errors.firstName.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="lastName"
            className="text-sm font-medium text-gray-700">
            Last Name
          </label>
          <div className="relative mt-1">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
              <UserIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
            </span>
            <input
              id="lastName"
              type="text"
              placeholder="Last Name"
              {...register("lastName", {
                ...requiredValidation("Last Name"),
                ...maxLengthValidation(
                  40,
                  "Last name must be less than 40 characters"
                ),
              })}
              className={`block w-full py-3 pl-10 pr-3 text-gray-900 placeholder-gray-400 border rounded-md shadow-sm appearance-none focus:outline-none focus:ring-2 sm:text-sm 
                ${
                  errors.lastName
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                }`}
            />
          </div>
          {errors.lastName && (
            <p className="mt-2 text-sm text-red-600">
              {errors.lastName.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="password"
            className="text-sm font-medium text-gray-700">
            Password
          </label>
          <div className="relative mt-1">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              {...register("password", {
                required: "Password is required",
                validate: (value: string) => {
                  const allMet = passwordCriteriaList.every(c => c.test(value));
                  return allMet || "Password does not meet all criteria";
                },
              })}
              className={classNames(
                errors.password
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                  : passwordValue &&
                      passwordCriteriaList.every(c => c.test(passwordValue))
                    ? "border-green-500 focus:ring-green-500 focus:border-green-500"
                    : "border-gray-300 focus:ring-indigo-500 focus:border-indigo-500",
                "block w-full py-3 pl-3 pr-10 text-gray-900 border rounded-md shadow-sm appearance-none focus:outline-none focus:ring-2 sm:text-sm"
              )}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              aria-label={showPassword ? "Hide password" : "Show password"}>
              {showPassword ? (
                <EyeSlashIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <EyeIcon className="w-5 h-5 text-gray-500" />
              )}
            </button>
          </div>
          {passwordValue && (
            <p
              className={classNames(
                isPasswordStrong ? "text-green-600" : "text-red-600",
                "mt-2 text-sm"
              )}>
              Password is {isPasswordStrong ? "strong" : "weak"}
            </p>
          )}
        </div>

        <ul className="space-y-1">
          {passwordCriteriaList.map(criterion => {
            const isMet = passwordCriteriaMet[criterion.id];
            return (
              <li
                key={criterion.id}
                className={classNames(
                  isMet ? "text-green-600" : "text-gray-500",
                  "flex items-center text-sm"
                )}>
                {isMet ? (
                  <CheckCircleIcon className="w-4 h-4 mr-2" />
                ) : (
                  <XCircleIcon className="w-4 h-4 mr-2 text-gray-400" />
                )}
                {criterion.text}
              </li>
            );
          })}
        </ul>

        <div className="flex items-center">
          <input
            id="marketingConsent"
            type="checkbox"
            {...register("marketingConsent")}
            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
          />
          <label
            htmlFor="marketingConsent"
            className="block ml-2 text-sm text-gray-900">
            I agree to receive updates, news, and offers from Foundry Connect.
          </label>
        </div>

        <button
          type="submit"
          disabled={!isValid || isCreateAccountLoading}
          className="w-full px-4 py-3 font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-300 disabled:cursor-not-allowed">
          <Spinner loading={isCreateAccountLoading}>Confirm</Spinner>
        </button>
      </form>

      <LoginLink />
    </div>
  );
};

export default AccountInfoForm;
